import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import ButtonCustom from 'components/ButtonCustom';
import CustomTableCell from 'components/CustomTableCell';
import { CustomTableFontCell } from 'components/CustomTableFontCell';
import GenericDialog from 'components/GenericDialog';
import WhatsNewModal from 'components/WhatsNewModal';
import { InspectionStatusBadge } from 'components/RevisionRowData';
import { StyledTableSortLabel } from 'components/TableSortedLabel';
import AuthContext from 'context/AuthContext';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useState, startTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { assignInspectorToRevision, getInspectionDashboard, getInspectorsFromOrganization, markInspectionViewed, updateInspectionStatus } from 'services/inspections-service';
import { USER_ROLES } from 'utils/constants';

// Generic Status Card Component
interface StatusCardProps {
  backgroundColor: string;
  title: string;
  value: number;
}

const StatusCard = ({ backgroundColor, title, value }: StatusCardProps) => (
  <Card
    sx={{
      backgroundColor,
      color: 'white',
      boxShadow: 'none'
    }}
  >
    <CardContent sx={{ textAlign: 'center', py: 2 }}>
      <Typography variant="h6" component="div" sx={{ fontSize: '18px' }}>
        {title}
      </Typography>
      <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mt: 1, fontSize: '36px' }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const statusFilterOptions = (t: any) => [
  { value: '', label: t('ALL') || 'All' },
  { value: 'awaiting', label: t('REVISION_INSPECTION_STATUS_AWAITING') },
  { value: 'pending', label: t('REVISION_INSPECTION_STATUS_PENDING') },
  { value: 'checked', label: t('REVISION_INSPECTION_STATUS_CHECKED') },

]

/** Fixed width so פעולות / Actions header lines up with row buttons in both LTR and RTL */
const INSPECTION_TABLE_ACTIONS_COL_WIDTH = 168

export default function InspectorInspectionsPage() {
  const [params] = useSearchParams()
  const urlParams = useParams()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [sortBy, setSortBy] = useState('updatedAt')
  const assignmentIdFromLink = params.get('assignmentId') || ''
  const { organizationId, role } = useContext(AuthContext)
  const { enqueueSnackbar } = useSnackbar()
  const { t,i18n } = useTranslation()
  const isRtl = i18n.language === 'he'
  // Use organizationId from URL params if admin is viewing a specific organization, otherwise use context
  const currentOrganizationId = urlParams.organizationId || organizationId

  const { data: dashboardData, isLoading, refetch } = useQuery(
    ['inspection-dashboard', status, currentOrganizationId],
    () => getInspectionDashboard(status || undefined, urlParams.organizationId),
    { refetchOnWindowFocus: true }
  )

  // Get all inspections for statistics (without status filter)
  const { data: allInspectionsData } = useQuery(
    ['inspection-dashboard-all', currentOrganizationId],
    () => getInspectionDashboard(undefined, urlParams.organizationId),
    { refetchOnWindowFocus: true }
  )

  const inspections = dashboardData?.data || []
  const allInspectionsForStats = allInspectionsData?.data || []
  const userRole = role
  const totalCount = dashboardData?.totalCount || 0

  // Get inspectors from current organization (for inspector managers)
  const { data: organizationInspectors } = useQuery(
    ['organization-inspectors', currentOrganizationId],
    () => getInspectorsFromOrganization(currentOrganizationId || ''),
    {
      enabled: userRole === USER_ROLES.INSPECTOR_MANAGER || userRole === USER_ROLES.ADMIN && !!currentOrganizationId
    }
  )

  const markViewedMut = useMutation((assignmentId: string) => markInspectionViewed(assignmentId), {
    onSuccess: () => queryClient.invalidateQueries('inspection-dashboard')
  })

  const updateStatusMut = useMutation(({ id, status, note }: { id: string, status: 'checked' | 'pending', note?: string }) => updateInspectionStatus(id, status, note), {
    onSuccess: () => queryClient.invalidateQueries('inspection-dashboard')
  })

  useEffect(() => {
    if (assignmentIdFromLink) {
      markViewedMut.mutate(assignmentIdFromLink)
    }
  }, [assignmentIdFromLink])


  const allRows = useMemo(() => (inspections || []).map((it: any) => ({
    id: it._id || it.id, // assignment id
    organizationName: it.organization?.company_name || '-',
    revisionId: it.revision?._id || it.revision?.id,
    projectId: it.project?._id || it.project?.id,
    projectName: it.project?.name || '-',
    projectType: it.revision?.project?.type || it.project?.type || '-',
    inspectorName: it.inspector?.name || it.inspector?.email || '-',
    files: it.revision?.files || {},
    grade: it.revision?.weighed_grade,
    rating: it.revision?.rating,
    status: it.inspectorStatus || it.status, // Use inspectorStatus, fallback to status for backward compatibility
    updatedAt: it.updatedAt,
    inspection: it // Pass the full inspection object for InspectionStatusBadge
  })), [inspections])

  const [openAssignModal, setOpenAssignModal] = useState(false)
  const [openStatusModal, setOpenStatusModal] = useState(false)
  const [selectedInspector, setSelectedInspector] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<any>('')
  const [selectedRevisionId, setSelectedRevisionId] = useState('')
  const [selectedAssignmentId, setSelectedAssignmentId] = useState('')
  const [selectedProjectId, setSelectedProjectId] = useState('')
  const [approvedWarningChecked, setApprovedWarningChecked] = useState(false)
  const isInspectorManager = userRole === USER_ROLES.INSPECTOR_MANAGER || userRole === USER_ROLES.ADMIN

  const handleRequestSort = (event: React.MouseEvent<unknown>, newOrderBy: string) => {
    const isAsc = sortBy === newOrderBy && sortOrder === 'asc'
    setSortOrder(isAsc ? 'desc' : 'asc')
    setSortBy(newOrderBy)
  }

  // Sorting function that handles different data types
  const getSortValue = (row: any, sortField: string) => {
    const value = row[sortField]

    switch (sortField) {
      case 'updatedAt':
        return new Date(value).getTime()
      case 'organizationName':
      case 'projectName':
      case 'projectType':
      case 'revisionId':
      case 'inspectorName':
      case 'status':
        return (value || '').toString().toLowerCase()
      default:
        return value || ''
    }
  }

  const sortedRows = useMemo(() => {
    const rowsToSort = [...allRows]

    if (sortBy) {
      rowsToSort.sort((a, b) => {
        const aValue = getSortValue(a, sortBy)
        const bValue = getSortValue(b, sortBy)

        if (aValue < bValue) {
          return sortOrder === 'asc' ? -1 : 1
        }
        if (aValue > bValue) {
          return sortOrder === 'asc' ? 1 : -1
        }
        return 0
      })
    }

    return rowsToSort
  }, [allRows, sortBy, sortOrder])

  // Filter rows based on search query (apply to sorted rows)
  const rows = useMemo(() => {
    if (!searchQuery.trim()) {
      return sortedRows
    }
    return sortedRows.filter(row =>
      row.revisionId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.organizationName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.projectName?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [sortedRows, searchQuery])

  const handleCloseModals = () => {
    setOpenAssignModal(false)
    setOpenStatusModal(false)
    setSelectedInspector('')
    setSelectedStatus('')
    setSelectedRevisionId('')
    setSelectedProjectId('')
    setSelectedAssignmentId('')
    setApprovedWarningChecked(false)
  }

  const inspectionStatusOptions = [
    { value: 'checked', label: t('REVISION_INSPECTION_STATUS_CHECKED') },
    { value: 'pending', label: t('REVISION_INSPECTION_STATUS_PENDING') }
  ]


 

  // Calculate statistics by inspectorStatus - always use all inspections regardless of filter
  const statusStats = useMemo(() => {
    const stats = {
      awaiting: 0,
      checked: 0,
      pending: 0
    }

    // Use all inspections for stats, not the filtered ones
    allInspectionsForStats.forEach((inspection: any) => {
      const status = (inspection.inspectorStatus || inspection.status)?.toLowerCase()
      if (status === 'awaiting') {
        stats.awaiting++
      } else if (status === 'checked') {
        stats.checked++
      } else if (status === 'pending') {
        stats.pending++
      }
    })

    return stats
  }, [allInspectionsForStats])


  return (
    <div>
      <WhatsNewModal />
      {/* Title Section */}
      <Box sx={{ height: "42px", alignContent: "center", mb: "42px", display: "flex", alignItems: "center" }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 'bold',
            color: '#2F3B49',
          }}
        >
           {t('INSPECTOR_DASHBOARD_TITLE')}
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} xs={12} mb={3}>
        <Grid item xs={12} sm={6} md={4}>
          <StatusCard
            backgroundColor="#ACACAC"
            title={t('REVISION_INSPECTION_STATUS_AWAITING')}
            value={statusStats.awaiting}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatusCard
            backgroundColor="#ff9800"
            title={t('REVISION_INSPECTION_STATUS_PENDING')}
            value={statusStats.pending}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatusCard
            backgroundColor="#1bb81e"
            title={t('REVISION_INSPECTION_STATUS_CHECKED')}
            value={statusStats.checked}
          />
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Grid container spacing={3} xs={12} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={t('ADMIN_INSPECTION_SEARCH_REVISION_ID')}
            name="searchQuery"
            value={searchQuery}
            sx={{
              width: '100%',
              '& .MuiInputBase-root': {
                height: '50px'
              }
            }}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('ADMIN_INSPECTION_SEARCH_REVISION_ID')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl sx={{ width: '100%' }}>
            <InputLabel id="status-label">{t('ADMIN_REVISION_JOB_STATUS')}</InputLabel>
            <Select
              labelId="status-label"
              value={status}
              label={t('ADMIN_REVISION_JOB_STATUS')}
              onChange={(e: any) => setStatus(e.target.value)}
              sx={{
                height: '50px'
              }}
            >
              {statusFilterOptions(t).map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'center' }}>

        </Grid>
      </Grid>

      <TableContainer>
        <Table size="small" sx={{ fontSize: '14px' }}>
          <TableHead>
            <TableRow sx={{ '&:hover': { boxShadow: 'none' } }}>
              <CustomTableFontCell sortDirection={sortBy === "updatedAt" ? sortOrder : false}>
                <StyledTableSortLabel
                  active={sortBy === "updatedAt"}
                  direction={sortBy === "updatedAt" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "updatedAt")}
                >
                  {t('INSPECTION_UPDATED_DATE')}
                  {sortBy === "updatedAt" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell sortDirection={sortBy === "organizationName" ? sortOrder : false}>
                <StyledTableSortLabel
                  active={sortBy === "organizationName"}
                  direction={sortBy === "organizationName" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "organizationName")}
                >
                  {t('INSPECTION_DETAILS_ORGANIZATION')}
                  {sortBy === "organizationName" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell sortDirection={sortBy === "projectName" ? sortOrder : false}>
                <StyledTableSortLabel
                  active={sortBy === "projectName"}
                  direction={sortBy === "projectName" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "projectName")}
                >
                  {t('INSPECTION_PROJECT')}
                  {sortBy === "projectName" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell sortDirection={sortBy === "projectType" ? sortOrder : false} sx={{ minWidth: '180px', width: '180px' }}>
                <StyledTableSortLabel
                  active={sortBy === "projectType"}
                  direction={sortBy === "projectType" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "projectType")}
                >
                  {t('PROJECT_FORM_TYPE')}
                  {sortBy === "projectType" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell  sortDirection={sortBy === "revisionId" ? sortOrder : false}>
                <StyledTableSortLabel
                  active={sortBy === "revisionId"}
                  direction={sortBy === "revisionId" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "revisionId")}
                >
                  {t('REVISION_REVISION_ID_COLUMN')}
                  {sortBy === "revisionId" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              {isInspectorManager && (
                <CustomTableFontCell sortDirection={sortBy === "inspectorName" ? sortOrder : false}>
                  <StyledTableSortLabel
                    active={sortBy === "inspectorName"}
                    direction={sortBy === "inspectorName" ? sortOrder : "asc"}
                    onClick={(event) => handleRequestSort(event, "inspectorName")}
                  >
                    {t('BUSINESS_SETTING_MEMBER_PERMISSION_INSPECTOR')}
                    {sortBy === "inspectorName" ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortOrder === "desc"
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                </CustomTableFontCell>
              )}
              <CustomTableFontCell sortDirection={sortBy === "status" ? sortOrder : false} sx={{ width: isRtl ? '110px' : '150px', maxWidth: isRtl ? '110px' : '150px' }}>
                <StyledTableSortLabel
                  active={sortBy === "status"}
                  direction={sortBy === "status" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "status")}
                >
                  {t('ADMIN_REVISION_JOB_STATUS')}
                  {sortBy === "status" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell
                sx={{
                  cursor: 'default !important',
                  width: INSPECTION_TABLE_ACTIONS_COL_WIDTH,
                  minWidth: INSPECTION_TABLE_ACTIONS_COL_WIDTH,
                  maxWidth: INSPECTION_TABLE_ACTIONS_COL_WIDTH,
                  fontSize: '14px',
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  boxSizing: 'border-box',
                }}
              >
                {t('ADMIN_REVISION_ACTIONS', { defaultValue: 'Actions' })}
              </CustomTableFontCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow
                key={r.id}
                sx={{ '&:hover': { border: 'none' } }}
              >
                {/* Updated Date */}
                <CustomTableCell sx={{ cursor: 'default !important', userSelect: 'text' }}>

                  {moment.utc(r.updatedAt).local().format('DD/MM/YY')}
                  <span className='time' style={{ marginLeft: '10px' }}></span>
                  {moment.utc(r.updatedAt).local().format('HH:mm')}

                </CustomTableCell>

                {/* Organization */}
                <CustomTableCell sx={{ cursor: 'default !important', userSelect: 'text' }}>
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>{r.organizationName}</Typography>
                </CustomTableCell>

                {/* Project */}
                <CustomTableCell sx={{ cursor: 'default !important', userSelect: 'text' }}>
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>{r.projectName}</Typography>
                </CustomTableCell>

                {/* Project Type */}
                <CustomTableCell sx={{ cursor: 'default !important', userSelect: 'text', minWidth: '180px', width: '180px' }}>
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>{r.projectType}</Typography>
                </CustomTableCell>

                {/* Revision ID */}
                <CustomTableCell sx={{ cursor: 'default !important', userSelect: 'text' }} dir="ltr">
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>{r.revisionId}</Typography>
                </CustomTableCell>

                {/* Inspector Name (only for inspector managers) */}
                {isInspectorManager && (
                  <CustomTableCell sx={{ cursor: 'default !important', userSelect: 'text' }}>
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>{r.inspectorName || '-'}</Typography>
                  </CustomTableCell>
                )}

                {/* Status */}
                <CustomTableCell sx={{ cursor: 'default !important', userSelect: 'text', width: '135px', maxWidth: '135px' }} >
                  <InspectionStatusBadge rtl={isRtl} inspection={r.inspection} />
                </CustomTableCell>

                {/* Actions - Button to Report */}
                <CustomTableCell
                  width={`${INSPECTION_TABLE_ACTIONS_COL_WIDTH}px`}
                  sx={{
                    cursor: 'default !important',
                    minWidth: INSPECTION_TABLE_ACTIONS_COL_WIDTH,
                    maxWidth: INSPECTION_TABLE_ACTIONS_COL_WIDTH,
                    textAlign: 'end',
                    verticalAlign: 'middle',
                    boxSizing: 'border-box',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      width: '100%',
                    }}
                  >
                    <ButtonCustom
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => {
                        startTransition(() => {
                          window.open(`/report/${r.revisionId}/check`, '_blank', 'noopener,noreferrer');
                        })
                      }}
                    >
                      {t('GO_TO_REPORT', { defaultValue: 'Go to Report' })}
                    </ButtonCustom>
                  </Box>
                </CustomTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>


      {/* Assign Inspector Modal */}
      <GenericDialog
        open={openAssignModal}
        onClose={handleCloseModals}
        title={`${t('INSPECTION_ASSIGNMENT_TITLE')} ${selectedRevisionId}`}
        subtitle={t('INSPECTOR_SELECTOR_ONLY_ONE_LABORATORY')}
        titleAlign="flex-start"
        maxWidth={false}
        fullWidth={false}
        paperSx={{
          width: '540px',
          marginTop: '5vh'
        }}
        onConfirm={async () => {
          try {
            if (!selectedInspector) {
              enqueueSnackbar(t('INSPECTION_PLEASE_SELECT_INSPECTOR'), { variant: 'error' })
              return
            }

            const assignment = {
              email: selectedInspector,
              assignmentType: 'inspector' as const
            }


            await assignInspectorToRevision(
              selectedRevisionId,
              selectedProjectId,
              currentOrganizationId || '',
              assignment
            )

            enqueueSnackbar(t('INSPECTOR_ASSIGNED_SUCCESSFULLY'), { variant: 'success' })
            refetch()
            handleCloseModals()
          } catch (error) {
            enqueueSnackbar(t('INSPECTION_ERROR_ASSIGNING_INSPECTOR'), { variant: 'error' })
          }
        }}
        confirmButtonText={t('ASSIGN_BUTTON')}
        cancelButtonText={t('CANCEL_BUTTON')}
      >
        <Box py={2} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', width: '100%' }}>
          <Typography variant="h3" sx={{ mb: 2, fontWeight: 600 }}>
            {t('INSPECTION_SELECT_INSPECTOR_EMAIL')}
          </Typography>
          <FormControl fullWidth sx={{ alignSelf: 'flex-start' }}>
            <InputLabel>{t('ASSIGN_INSPECTOR_SELECTOR_PLACEHOLDER')}</InputLabel>
            <Select
              value={selectedInspector}
              onChange={(e) => setSelectedInspector(e.target.value)}
              label={t('ASSIGN_INSPECTOR_SELECTOR_PLACEHOLDER')}
              sx={{ textAlign: 'left' }}

            >
              {organizationInspectors?.map((inspector) => (
                <MenuItem key={inspector.id} value={inspector.email}>
                  {inspector.name} {/* ({inspector.email}) */}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </GenericDialog>

      {/* Update Status Modal */}
      <GenericDialog
        open={openStatusModal}
        onClose={handleCloseModals}
        title={`${t('INSPECTION_STATUS_UPDATE_TITLE')} ${selectedRevisionId}`}
        subtitle={t('INSPECTION_STATUS_UPDATE_SUBTITLE')}
        titleAlign="flex-start"
        maxWidth={false}
        fullWidth={false}
        paperSx={{
          width: '540px',
          marginTop: '5vh'
        }}
        onConfirm={async () => {
          try {
            if (!selectedStatus) {
              enqueueSnackbar(t('INSPECTION_PLEASE_SELECT_STATUS'), { variant: 'error' })
              return
            }
            if (selectedStatus === 'checked' && !approvedWarningChecked) {
              enqueueSnackbar(t('INSPECTION_STATUS_APPROVED_CHECKBOX_REQUIRED'), { variant: 'error' })
              return
            }
            await updateInspectionStatus(selectedAssignmentId, selectedStatus)
            enqueueSnackbar(t('INSPECTION_STATUS_UPDATED_SUCCESS'), { variant: 'success' })
            refetch()
            handleCloseModals()
          } catch (error) {
            enqueueSnackbar(t('INSPECTION_ERROR_UPDATING_STATUS'), { variant: 'error' })
          }
        }}
        confirmButtonText={t('ASSIGN_BUTTON')}
        cancelButtonText={t('CANCEL_BUTTON')}
      >
        <Box py={2} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', width: '100%' }}>
          <Typography variant="h3" sx={{ mb: 2, fontWeight: 600 }}>
            {t('INSPECTION_SELECT_STATUS')}
          </Typography>
          <FormControl fullWidth sx={{ alignSelf: 'flex-start' }}>
            <InputLabel>{t('INSPECTOR_SELECTOR_PLACEHOLDER')}</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              label={t('INSPECTOR_SELECTOR_PLACEHOLDER')}
              sx={{ textAlign: 'left' }}
            >
              {inspectionStatusOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedStatus === 'checked' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={approvedWarningChecked}
                  onChange={(e) => setApprovedWarningChecked(e.target.checked)}
                  color="primary"
                />
              }
              label={t('INSPECTION_STATUS_APPROVED_WARNING')}
              sx={{ mt: 2, alignSelf: 'flex-start' }}
            />
          )}
        </Box>
      </GenericDialog>

    </div>
  )
}


